import { useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import MandatorySign from "../Components/MandatorySign";
import { CiCircleAlert } from "react-icons/ci";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;



const SaveActionsModal = ({
    account_id,
    show,
    handleClose,
    actionData,
    singleOrgData,
    steps,
    fetchData
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate()
    const { org_id, act_id } = useParams()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        organization_ids: [],
        trigger_type: "one_time",
        chron_settings: {
            interval: "days",
            value: 1,
            hour: 0,
            minute: 0,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    });

    const intervalOptions = [
        { label: t.actions.minutes, value: "minutes" },
        { label: t.actions.hours, value: "hours" },
        { label: t.actions.days, value: "days" },
        { label: t.actions.weeks, value: "weeks" },
        { label: t.actions.months, value: "months" },
    ];

    useEffect(() => {
        if (actionData && show) {
            setFormData({
                name: actionData?.name || "",
                description: actionData?.description || "",
                organization_ids: actionData?.organizations || [`test-${org_id}`],
                trigger_type: actionData?.trigger_type || "one_time",
                chron_settings: {
                    interval: actionData.chron_settings?.interval || "days",
                    value: actionData.chron_settings?.value || 1,
                    hour: actionData.chron_settings?.hour || 0,
                    minute: actionData.chron_settings?.minute || 0,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
            });
        }
    }, [actionData, show, act_id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSetTest = () => {
        setFormData(prev => ({
            ...prev,
            organization_ids: [`test-${org_id}`]
        }));
    };

    const handleOrgSelect = (orgId) => {
        setFormData(prev => {
            const alreadySelected = prev.organization_ids.includes(orgId);

            const newIds = prev.organization_ids.filter(id => id !== `test-${org_id}`);

            return {
                ...prev,
                organization_ids: alreadySelected
                    ? newIds.filter(id => id !== orgId)
                    : [...newIds, orgId]
            };
        });
    };


    const handleSave = async () => {
        if (!formData.name.trim()) {
            setErrorMessage({ message: t.actions.action_title_req, display: true });
            return;
        }

        if (!formData.description.trim()) {
            setErrorMessage({ message: t.actions.action_desc_req, display: true });
            return;
        }

        if (formData.description.length > 300) {
            setErrorMessage({ message: t.actions.desc_too_long, display: true });
            return;
        }

        if (formData.trigger_type == "scheduled") {

            if (!formData.chron_settings.interval) {
                setErrorMessage({ message: t.actions.interval_req, display: true });
                return;
            }

            if (!formData.chron_settings.value) {
                setErrorMessage({ message: t.actions.repeat_req, display: true });
                return;
            }
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });


        const cleanedStepToSend = steps.map(step => ({
            id: step.id,
            step_name: step.step_name,
            ability: step.ability?.id,
            endpoints: (step.endpoints || []).map(ep => ({
                endpoint: ep.endpoint,
                values: ep.values
            }))
        }));

        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/save_action`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    act_id,
                    name: formData.name,
                    description: formData.description,
                    organization_ids: formData.organization_ids,
                    trigger_type: formData.trigger_type,
                    chron_settings: formData.chron_settings,
                    steps: steps
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.status == "error") {
                    if (data.message == "insufficient_funds") {
                        throw new Error(t.insufficient_funds);
                    }
                } else {
                    throw new Error(data.error || "Something went wrong");
                }
            }


            if (data.status == "success") {
                navigate(`/e8console/organizations/${org_id}/actions/${data.act_id}`)
            }

            closeModal();
            fetchData()
        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setErrorMessage({ message: "", display: false });
        setLoadingSave(false);
        setFormData({
            name: "",
            description: "",
            organization_ids: [`test-${org_id}`],
            trigger_type: "one_time",
            chron_settings: {
                interval: "days",
                value: 1,
                hour: 0,
                minute: 0,
            }
        })
    };


    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">{t.actions.save_run_action}</p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    <div className="label_input">
                        <label className="input_label">{t.sidebar.organizations} <MandatorySign message={t.mandatory_field} /></label>
                        <Dropdown>
                            <Dropdown.Toggle
                                as="div"
                                className="register_form_first fade-in"
                            >
                                {formData.organization_ids.length > 0
                                    ? formData.organization_ids.map(id => {
                                        if (id === `test-${org_id}`) return "Personal mode";
                                        if (id === "public") return "Public";
                                        if (singleOrgData && singleOrgData.id === id) return singleOrgData.name;
                                        return id; // fallback
                                    }).join(", ")
                                    : t.select
                                }
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div className="dropdown_container fade-in">
                                    <button
                                        className="settings_link"
                                        onClick={handleSetTest}
                                    >
                                        <i>
                                            {formData.organization_ids.includes(`test-${org_id}`)
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />}
                                        </i>
                                        Personal mode
                                    </button>
                                    {/* <button
                                        className="settings_link"
                                        onClick={handleSetPublic}
                                    >
                                        <i>
                                            {formData.organization_ids.includes("public")
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />}
                                        </i>
                                        Public to anyone
                                    </button> */}
                                    {singleOrgData && (
                                        <button
                                            key={singleOrgData.id}
                                            onClick={() => handleOrgSelect(singleOrgData.id)}
                                            className="settings_link"
                                        >
                                            <i>
                                                {formData.organization_ids.includes(singleOrgData.id)
                                                    ? <MdOutlineCheckCircle />
                                                    : <MdOutlineCircle />}
                                            </i>
                                            {singleOrgData.name}
                                        </button>
                                    )}
                                    {/* <button
                                        onClick={() => setOpenOrgModal(true)}
                                        className="settings_link"
                                    >
                                        <i><BiPlus /></i> Create new organization
                                    </button> */}
                                </div>
                            </Dropdown.Menu>

                        </Dropdown>
                        <div className="selected_items_container">
                            {formData.organization_ids.map(id => {
                                if (id === "public") {
                                    return (
                                        <p className="role_warning_text" key={id}>
                                            <i><CiCircleAlert /></i>{" "}
                                            {t.actions.public_action_warning}
                                        </p>
                                    );
                                }

                                if (id === `test-${org_id}`) {
                                    return (
                                        <p className="role_warning_text" key={id}>
                                            <i><CiCircleAlert /></i>{" "}
                                            {t.actions.private_action_warning}
                                        </p>
                                    );
                                }

                                return null;
                            })}
                        </div>
                    </div>

                    <div className="label_input">
                        <label className="input_label">{t.title} <MandatorySign message={t.mandatory_field} /></label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Ex. Post sales automation"
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>
                    <div className="label_input">
                        <label className="input_label">{t.description} <MandatorySign message={t.mandatory_field} /></label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder={t.actions.desc_placeholder}
                            className="register_form_first textarea_reg fade-in"
                            autoComplete="off"
                        />
                    </div>

                    <div className="persona_reg">
                        <div className="label_input">
                            <label className="input_label">{t.actions.trigger_type} <MandatorySign message={t.mandatory_field} /></label>
                            <Dropdown drop="up">
                                <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                    {formData.trigger_type === "one_time" ? t.actions.one_time_run : t.actions.scheduled}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="custom_dropdown_menu">
                                    <Dropdown.Item
                                        onClick={() => setFormData({ ...formData, trigger_type: "scheduled" })}
                                        className={`settings_link ${formData.trigger_type == "scheduled" ? "settings_link_selected" : ""}`}
                                    >
                                        {t.actions.scheduled}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setFormData({ ...formData, trigger_type: "one_time", chron_settings: {} })}
                                        className={`settings_link ${formData.trigger_type == "one_time" ? "settings_link_selected" : ""}`}
                                    >
                                        {t.actions.one_time_run}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                    {formData.trigger_type === "scheduled" && (
                        <div className="scheduled_settings">

                            <div className="persona_reg">
                                <div className="label_input">
                                    <label className="input_label">{t.actions.interval}</label>

                                    <Dropdown>
                                        <Dropdown.Toggle as="div" className="register_form_first fade-in pointer">
                                            {intervalOptions.find(
                                                opt => opt.value === formData.chron_settings?.interval
                                            )?.label || t.select}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="custom_dropdown_menu">
                                            {intervalOptions.map(opt => (
                                                <Dropdown.Item
                                                    key={opt.value}
                                                    onClick={() =>
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            chron_settings: {
                                                                ...prev.chron_settings,
                                                                interval: opt.value
                                                            }
                                                        }))
                                                    }
                                                    className={`settings_link ${formData.chron_settings?.interval == opt.value && "settings_link_selected"} `}
                                                >
                                                    {opt.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>

                                <div className="label_input">
                                    <label className="input_label">{t.actions.repeat_every}</label>
                                    <input
                                        type="number"
                                        min={1}
                                        className="register_form_first fade-in"
                                        value={formData.chron_settings?.value || 1}
                                        onChange={(e) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                chron_settings: {
                                                    ...prev.chron_settings,
                                                    value: Number(e.target.value)
                                                }
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            {!["minutes", "hours"].includes(formData.chron_settings?.interval) && (
                                <div className="persona_reg">
                                    <div className="label_input">
                                        <label className="input_label">{t.actions.hours} (0-23)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={23}
                                            className="register_form_first fade-in"
                                            value={formData.chron_settings?.hour || 0}
                                            onChange={(e) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    chron_settings: {
                                                        ...prev.chron_settings,
                                                        hour: Number(e.target.value)
                                                    }
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="label_input">
                                        <label className="input_label">{t.actions.minutes} (0-59)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={59}
                                            className="register_form_first fade-in"
                                            value={formData.chron_settings?.minute || 0}
                                            onChange={(e) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    chron_settings: {
                                                        ...prev.chron_settings,
                                                        minute: Number(e.target.value)
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? <Spinner loading={true} /> : t.save}
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SaveActionsModal;